/**
 * Fonte central de versão do INSPECT IA — Comando "Padronização Global de
 * Nome + Versionamento Dinâmico" (2026-09-28).
 *
 * O NOME é fixo e nunca leva a versão embutida (ver `product-identity.ts`).
 * A VERSÃO é metadado de release, separado, e só muda por ação explícita
 * (bump manual do semver em `package.json`) — nunca sozinha a cada
 * build/restart/deploy. O NÚMERO DE BUILD é o que pode variar a cada
 * compilação (timestamp de build), sem tocar a versão.
 *
 * Este módulo NÃO lê `package.json` diretamente (ficaria acoplado a Node.js
 * e quebraria no navegador). Cada lado da aplicação injeta a versão pela via
 * que já existe:
 *   - Navegador: `__APP_VERSION__`/`__APP_BUILD_TIME__`/`__APP_BACKEND_ENV__`
 *     (substituição textual via `define` no `vite.config.ts`, já usada por
 *     `browser-settings-probe.ts` e `browser-platform.ts`).
 *   - Servidor: `readFileSync("package.json")` (já usado por
 *     `server-settings-probe.ts`) + `process.env.APP_ENV`.
 *
 * `resolveAppVersion(input)` é a única função que monta o objeto `AppVersion`
 * — os dois lados chamam ela com os valores que já têm à mão, em vez de cada
 * um formatar a própria string de exibição.
 */

/** Status de release — RC nunca deve aparecer para o usuário final em produção. */
export type ReleaseStatus = "DEVELOPMENT" | "RC" | "RELEASED" | "DEPRECATED";

/**
 * Ambiente de implantação (deploy) — NÃO confundir com:
 *   - o enum `Environment` de `src/infrastructure/config/config.ts`
 *     (development/test/production, controlado por `NODE_ENV`, parte do
 *     carregador de config da camada SQLite não conectada pelo app real);
 *   - o campo `environment: "HOMOLOGACAO"|"PRODUCAO"` do domínio de
 *     integração ICP (`system-settings.ts`), que é um dado de negócio do
 *     certificado digital, sem relação com onde o software está implantado.
 * Este aqui é o mesmo sinal de `APP_ENV`/`__APP_BACKEND_ENV__` que já governa
 * `browser-platform.ts` (modo backend) e `infra/server.js`.
 */
export type DeploymentEnvironment = "development" | "homologation" | "production" | "local";

export interface AppVersion {
  /** Nome fixo do produto — nunca leva a versão embutida. */
  readonly productName: string;
  /** Semver puro, fonte: `package.json` (`"version"`). */
  readonly version: string;
  /** Formato de exibição derivado do semver: 1.0.0→"V-1", 1.1.0→"V-1.1", 1.1.2→"V-1.1.2". */
  readonly displayVersion: string;
  /** Identifica o BUILD (não a versão) — timestamp de build ou hash de commit. Pode variar sem que a versão mude. */
  readonly buildNumber: string;
  /** Data do release (da versão, não do build) — "não informada" quando ausente. */
  readonly releaseDate: string;
  /** Ambiente de implantação real (ver nota acima — nunca os outros dois "environment"). */
  readonly environment: DeploymentEnvironment;
  /** RC nunca deve ser exibido a usuário final fora de DEV/HOMOLOG. */
  readonly releaseStatus: ReleaseStatus;
}

/** Metadado de release conhecido — alimenta a versão atual e, futuramente, o histórico de releases (§ VERSÃO-09). */
export interface ReleaseHistoryEntry {
  readonly version: string;
  readonly displayVersion: string;
  readonly releaseDate: string;
  readonly status: ReleaseStatus;
  readonly summary: string;
}

/**
 * Registro real de releases — só entradas verdadeiras, nunca inventadas
 * (regra do comando: "resumo real, nunca inventado"). Uma entrada por bump
 * de versão em `package.json`.
 */
export const RELEASE_HISTORY: readonly ReleaseHistoryEntry[] = [
  {
    version: "1.0.0",
    displayVersion: "V-1",
    releaseDate: "2026-09-28",
    status: "RELEASED",
    summary: "Padronização de nome (INSPECT IA, sem versão embutida) e primeira fonte central de versionamento dinâmico."
  }
];

/** 1.0.0 → "V-1" · 1.1.0 → "V-1.1" · 1.1.2 → "V-1.1.2" · 2.0.0 → "V-2". Patch/minor só aparecem quando não são zero. */
export function toDisplayVersion(semver: string): string {
  const match = /^(\d+)\.(\d+)\.(\d+)/.exec(semver.trim());
  if (!match) return `V-${semver}`;
  const [, major, minor, patch] = match as unknown as [string, string, string, string];
  let display = `V-${major}`;
  if (minor !== "0" || patch !== "0") display += `.${minor}`;
  if (patch !== "0") display += `.${patch}`;
  return display;
}

function resolveReleaseStatus(environment: DeploymentEnvironment, explicit?: ReleaseStatus): ReleaseStatus {
  if (explicit) return explicit;
  const latest = RELEASE_HISTORY[RELEASE_HISTORY.length - 1];
  if (latest) return latest.status;
  return environment === "production" ? "RELEASED" : "DEVELOPMENT";
}

export interface ResolveAppVersionInput {
  readonly version?: string | null;
  readonly buildNumber?: string | null;
  readonly environment?: DeploymentEnvironment | string | null;
  readonly releaseStatus?: ReleaseStatus;
}

const KNOWN_ENVIRONMENTS: readonly DeploymentEnvironment[] = ["development", "homologation", "production", "local"];

function normalizeEnvironment(value: string | null | undefined): DeploymentEnvironment {
  const normalized = (value ?? "").toLowerCase();
  return (KNOWN_ENVIRONMENTS as readonly string[]).includes(normalized)
    ? (normalized as DeploymentEnvironment)
    : "development";
}

declare const __APP_VERSION__: string | undefined;
declare const __APP_BUILD_TIME__: string | undefined;
declare const __APP_GIT_COMMIT__: string | undefined;

export function getRuntimeBuildInfo(): {
  commit: string;
  buildTimestamp: string;
  appVersion: string;
  asset: string;
} {
  const version = typeof __APP_VERSION__ !== "undefined" && __APP_VERSION__ ? __APP_VERSION__ : "1.0.0";
  const buildTimestamp = typeof __APP_BUILD_TIME__ !== "undefined" && __APP_BUILD_TIME__ ? __APP_BUILD_TIME__ : "2026-10-03T00:00:00.000Z";
  const commit = typeof __APP_GIT_COMMIT__ !== "undefined" && __APP_GIT_COMMIT__ ? __APP_GIT_COMMIT__ : "796b37183795aba399c772dd69b0d2211b6b2c91";
  let asset = "";
  if (typeof document !== "undefined") {
    const scriptEl = document.querySelector('script[src*="/assets/index-"]') as HTMLScriptElement | null;
    if (scriptEl && scriptEl.src) {
      asset = scriptEl.src.split("/").pop() || "";
    }
  }
  return {
    commit,
    buildTimestamp,
    appVersion: version,
    asset
  };
}

/** Única função que monta o `AppVersion` — navegador e servidor chamam com o que já leem hoje, sem duplicar formatação. */
export function resolveAppVersion(input: ResolveAppVersionInput = {}): AppVersion {
  const version = input.version && input.version.trim() ? input.version.trim() : "0.0.0";
  const environment = normalizeEnvironment(input.environment ?? null);
  const latestRelease = RELEASE_HISTORY.find(entry => entry.version === version) ?? RELEASE_HISTORY[RELEASE_HISTORY.length - 1];
  return {
    productName: "INSPECT IA",
    version,
    displayVersion: toDisplayVersion(version),
    buildNumber: input.buildNumber && input.buildNumber.trim() ? input.buildNumber.trim() : "não informado",
    releaseDate: latestRelease?.releaseDate ?? "não informada",
    environment,
    releaseStatus: resolveReleaseStatus(environment, input.releaseStatus)
  };
}
